import {
  AnalyticsManager,
  IReactiveModel,
  MicrofrontendManager,
  RecognitionManager,
  ViewModel,
} from '@descomplica/microfrontend-core';
import { action, makeObservable, observable } from 'mobx';

import { GetAccountCollaboration } from '@/modules/platform/application/collaborations/get-account.collaboration';
import { GetAccountGrantsCollaboration } from '@/modules/platform/application/collaborations/get-account-grants.collaboration';

import type { AccountDto, AccountGrantsDto } from '../application/interfaces/pegasus-io.gateway';

@ViewModel({ singleton: true })
export class MicrofrontendViewModel implements IReactiveModel {
  @observable isLoading = true;
  @observable error: Error;
  @observable account: AccountDto;
  @observable accountGrants: AccountGrantsDto = {} as AccountGrantsDto;

  constructor(
    private readonly getAccountCollaboration: GetAccountCollaboration,
    private readonly getAccountGrantsCollaboration: GetAccountGrantsCollaboration,
    private readonly analyticsManager: AnalyticsManager,
    private readonly recognitionManager: RecognitionManager,
  ) {
    makeObservable(this);
  }

  @action
  async onInit() {
    try {
      await this.initializeMicrofrontend();
      await this.identifyCurrentAccount();
    } catch (error) {
      console.error(error);
      this.error = error;
    } finally {
      this.isLoading = false;
    }
  }

  @action
  private async initializeMicrofrontend() {
    await MicrofrontendManager.init({
      pegasusUri: process.env.REACT_APP_PEGASUS_IO_URL,
      caching: {
        namespace: 'descomplica-io-platform-ui',
      },
      settings: {
        launchDarklyClientId: process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID,
      },
      analytics: {
        isEnabled: true,
        mixpanelToken: process.env?.REACT_APP_MIXPANEL_TOKEN,
        // TODO: Include Hubspot
        //hubspotToken: process.env?.REACT_APP_HUBSPOT_TOKEN,
      },
      recognition: {
        isEnabled: true,
        forceRedirectToSignIn: true,
        forceReautenticate: true,
        disableRedirectForPages: ['/signin', '/signup', '/reset-password', '/sso'],
        signInUrl: process.env?.REACT_APP_RECOGNITION_SIGN_IN_URL,
        reauthenticateUrl: process.env?.REACT_APP_RECOGNITION_REAUTHENTICATE_URL,
      },
    });
  }

  async identifyCurrentAccount() {
    try {
      const hasToken = this.recognitionManager.getAccessToken();
      if (!hasToken) {
        return;
      }
      await this.getAccount();
      await this.getAccountGrants();
      this.analyticsIdentify();
    } catch (error) {
      console.error(error);
      this.error = error;
    }
  }

  @action
  async getAccount() {
    try {
      const { data: account } = await this.getAccountCollaboration.run();
      this.account = account;
    } catch (error) {
      if (error.response.status !== 401) {
        throw error;
      }
    }
  }

  @action
  async getAccountGrants() {
    try {
      const { data } = await this.getAccountGrantsCollaboration.run();
      this.accountGrants = data ?? ({} as AccountGrantsDto);
    } catch (error) {
      if (error.response.status !== 401) {
        throw error;
      }
    }
  }

  @action
  analyticsIdentify() {
    try {
      this.analyticsManager.identify({
        id: this.account.registrationCode,
        email: this.account.email,
        name: this.account.name,
      });
    } catch (err) {
      console.log(err);
    }
  }

  @action
  analyticsTrackPageView = () => {
    try {
      this.analyticsManager.trackPageView();
    } catch (err) {
      console.warn('Error while trying Tracking Page View');
    }
  };

  @action
  clearAccount() {
    this.account = null;
    this.accountGrants = null;

    this.analyticsManager.reset();
  }
}
